// The letter sheet label designs (Classic, Price-first, Minimal, Retail) and their sizes. Ported unchanged from
// type2-letter-sheets.html: the parity suite compares every exported label pixel for pixel with the old app's.
import type { DrawOptions, LabelData, PageDims, Scale } from '../model/types';
import { _C39, _hasSlot, qrAbs, fmt$, TITLE_STEPS, saleBadge } from './common';

export const LETTER_W = 816;   // px at 96 PPI
export const LETTER_H = 1056;
export const MARGIN   = 40;    // 0.42" page margin
export const GAP      = 10;    // gap between labels (cut guide space)
export const LAYOUT_GRID: Record<number, [number, number]> = { 1:[1,1], 2:[1,2], 4:[2,2], 8:[2,4] };

function code39Barcode(rawText: string, maxW: number, barH: number): string {
  if(!rawText) return '';
  // Strip to valid Code 39 chars (uppercase)
  // eslint-disable-next-line no-useless-escape -- kept character for character as the original page had it
  const clean = rawText.toUpperCase().replace(/[^0-9A-Z\-\.\s\$\/\+\%]/g,'');
  if(!clean) return '';
  const encoded = '*' + clean + '*';

  // N = narrow unit in px, W = wide unit = 3N
  // Total units per char = 15N; inter-char gap = 1N
  const nChars = encoded.length;
  const totalUnits = nChars * 15 + (nChars - 1);
  const N = Math.max(1, Math.floor(maxW / totalUnits));
  const W = N * 3;

  const els = [];
  let totalW = 0;
  for(let ci = 0; ci < encoded.length; ci++){
    if(ci > 0){ els.push({bar:false, w:N}); totalW += N; }   // inter-char gap
    const pat = _C39[encoded[ci]!] || _C39['0']!;
    for(let i = 0; i < 9; i++){
      const wide = pat[i] === '1';
      const bar  = i % 2 === 0;   // 0,2,4,6,8 are bars; 1,3,5,7 are spaces
      const w = wide ? W : N;
      els.push({bar, w});
      totalW += w;
    }
  }

  const divs = els.map(e =>
    `<div style="width:${e.w}px;height:${barH}px;background:${e.bar?'#000':'#fff'};flex-shrink:0;"></div>`
  ).join('');
  return `<div style="display:flex;overflow:hidden;width:${totalW}px;height:${barH}px;">${divs}</div>`;
}

function extraRow(k1: string, v1: string, k2: string, v2: string, f: Scale, p: number, FO: string): string {
  if(!k1&&!k2) return '';
  const items = [];
  if(k1&&v1) items.push(`<span style="font-family:${FO};font-size:${f(11)}px;color:#222;font-weight:600;text-transform:uppercase;letter-spacing:1px;">${k1}: </span><span style="font-family:${FO};font-size:${f(11)}px;font-weight:700;">${v1}</span>`);
  if(k2&&v2) items.push(`<span style="font-family:${FO};font-size:${f(11)}px;color:#222;font-weight:600;text-transform:uppercase;letter-spacing:1px;">${k2}: </span><span style="font-family:${FO};font-size:${f(11)}px;font-weight:700;">${v2}</span>`);
  return `<div style="margin-top:${Math.round(3*p/10)}px;display:flex;gap:${p}px;flex-wrap:wrap;">${items.join('')}</div>`;
}

function priceBlock(v: LabelData, f: Scale, s: number, baseSize: number, FC: string, FO: string): string {
  if(!v.price) return '';
  const compareHtml = v.compare
    ? `<span style="font-family:${FO};font-size:${f(baseSize*0.57)}px;font-weight:500;color:#333;text-decoration:line-through;letter-spacing:0;margin-left:${Math.round(4*s)}px;vertical-align:middle;">${fmt$(v.compare)}</span>`
    : '';
  return `${saleBadge(v, Math.max(10, f(baseSize * SALE_K)), s, FC)}<span style="font-family:${FC};font-size:${f(baseSize)}px;font-weight:400;font-style:normal;letter-spacing:-1px;line-height:1;">${fmt$(v.price)}</span>${compareHtml}`;
}

const PRICE_BASE = 82;
const SALE_K     = 0.2;
function titleSize(name: string, base: number): number {
  const ceil  = PRICE_BASE * 0.57;
  const floor = Math.max(11, 12, 13, PRICE_BASE * SALE_K);
  const len = (name || '').length;
  const k = TITLE_STEPS.find(([max]) => len <= max)![1];
  return Math.min(ceil - 1, Math.max(floor + 1, base * k));
}

function tmplClassic(v: LabelData, px: number, py: number, s: number, f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number): string {
  const headerH = Math.round(20*s);
  const footerH = Math.round(18*s);
  const barH    = Math.round(24*s);
  const bodyT   = headerH;
  const bodyB   = footerH;
  const slot    = _hasSlot(qrSrc, v.direction);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};border:2px solid #000;box-sizing:border-box;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;right:0;height:${headerH}px;background:#000;color:#fff;display:flex;align-items:center;justify-content:space-between;padding:0 ${p}px;">
      <span style="font-family:${FC};font-size:${f(12)}px;font-weight:400;font-style:normal;letter-spacing:${Math.round(2*s)}px;text-transform:uppercase;">${v.store||'STORE NAME'}</span>
      ${v.sku?`<span style="font-family:${FC};font-size:${f(11)}px;font-weight:400;font-style:normal;opacity:.7;letter-spacing:1px;">SKU: ${v.sku}</span>`:''}
    </div>

    <div style="position:absolute;top:${bodyT}px;left:0;right:0;bottom:${bodyB}px;display:flex;align-items:stretch;gap:${p}px;padding:${p}px;overflow:hidden;">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          <div data-title style="font-family:${FC};font-size:${f(titleSize(v.name,26))}px;font-weight:400;font-style:normal;line-height:1.2;margin-bottom:${Math.round(2*s)}px;">${v.name||'Product Name'}</div>
          ${v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${f(11)}px;color:#222;font-weight:600;line-height:1.45;">${v.desc}</div>`:''}
          ${extraRow(v.ex1k,v.ex1v,v.ex2k,v.ex2v,f,p,FO)}
        </div>
        <div>
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(2*s)}px;">${priceBlock(v,f,s,82,FC,FO)}</div>`:''}
          ${v.sku?code39Barcode(v.sku,barcodeW,barH):''}
          ${v.sku?`<div style="font-family:${FC};font-size:${f(13)}px;font-weight:400;font-style:normal;letter-spacing:${Math.round(2*s)}px;color:#222;margin-top:2px;">${v.sku.split('').join(' ')}</div>`:''}
        </div>
      </div>
      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}
    </div>

    <div style="position:absolute;bottom:0;left:0;right:0;height:${footerH}px;background:#f0f0f0;border-top:2px solid #000;display:flex;align-items:center;justify-content:space-between;padding:0 ${p}px;">
      <span style="font-family:${FC};font-size:${f(10)}px;font-weight:400;font-style:normal;color:#888;letter-spacing:1px;text-transform:uppercase;">${v.store||''}</span>
      <span style="font-family:${FO};font-size:${f(8)}px;color:#888;letter-spacing:1px;">${new Date().toLocaleDateString()}</span>
    </div>

  </div>`;
}

function tmplPrice(v: LabelData, px: number, py: number, s: number, f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number): string {
  const stripeW = Math.round(6*s);
  const slot    = _hasSlot(qrSrc, v.direction);
  const innerL  = stripeW + p;
  const barH    = Math.round(22*s);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};border:2px solid #000;box-sizing:border-box;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;width:${stripeW}px;bottom:0;background:#000;"></div>

    <div style="position:absolute;top:0;left:${innerL}px;right:${p}px;bottom:0;display:flex;align-items:stretch;gap:${p}px;padding:${p}px 0;overflow:hidden;">

      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          ${v.store?`<div style="font-family:${FC};font-size:${f(11)}px;font-weight:400;font-style:normal;letter-spacing:3px;text-transform:uppercase;color:#888;margin-bottom:${Math.round(3*s)}px;">${v.store}</div>`:''}
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(3*s)}px;">${priceBlock(v,f,s,82,FC,FO)}</div>`:''}
          <div data-title style="font-family:${FC};font-size:${f(titleSize(v.name,24))}px;font-weight:400;font-style:normal;line-height:1.2;">${v.name||'Product Name'}</div>
          ${v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${f(11)}px;color:#222;font-weight:600;margin-top:${Math.round(2*s)}px;line-height:1.4;">${v.desc}</div>`:''}
          ${extraRow(v.ex1k,v.ex1v,v.ex2k,v.ex2v,f,p,FO)}
        </div>
        ${v.sku?`<div style="border-top:1px solid #ddd;padding-top:${Math.round(4*s)}px;">
          ${code39Barcode(v.sku,barcodeW,barH)}
          <div style="font-family:${FC};font-size:${f(13)}px;font-weight:400;font-style:normal;color:#333;letter-spacing:2px;margin-top:2px;">${v.sku}</div>
        </div>`:''}
      </div>

      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}

    </div>

  </div>`;
}

function tmplMinimal(v: LabelData, px: number, py: number, s: number, f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number): string {
  const slot  = _hasSlot(qrSrc, v.direction);
  const barH  = Math.round(24*s);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};box-sizing:border-box;border:1px solid #999;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:stretch;gap:${p}px;padding:${p}px;overflow:hidden;">

      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          ${v.store?`<div style="font-family:${FC};font-size:${f(10)}px;font-weight:400;font-style:normal;letter-spacing:3px;text-transform:uppercase;color:#999;margin-bottom:${Math.round(4*s)}px;">${v.store}</div>`:''}
          <div data-title style="font-family:${FC};font-size:${f(titleSize(v.name,26))}px;font-weight:400;font-style:normal;line-height:1.15;">${v.name||'Product Name'}</div>
          ${v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${f(11)}px;color:#222;font-weight:600;margin-top:${Math.round(3*s)}px;line-height:1.4;">${v.desc}</div>`:''}
          ${extraRow(v.ex1k,v.ex1v,v.ex2k,v.ex2v,f,p,FO)}
        </div>
        <div>
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(2*s)}px;">${priceBlock(v,f,s,82,FC,FO)}</div>`:''}
          ${v.sku?`<div>${code39Barcode(v.sku,barcodeW,barH)}</div>`:''}
          ${v.sku?`<div style="font-family:${FC};font-size:${f(13)}px;font-weight:400;font-style:normal;color:#333;letter-spacing:2px;margin-top:2px;">${v.sku}</div>`:''}
        </div>
      </div>

      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}

    </div>

  </div>`;
}

function tmplRetail(v: LabelData, px: number, py: number, s: number, f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number): string {
  const headerH = Math.round(22*s);
  const footerH = Math.round(16*s);
  const barH    = Math.round(26*s);

  function extraStacked(k1: string, v1: string, k2: string, v2: string){
    if(!k1&&!k2) return '';
    let rows = '';
    if(k1&&v1) rows += `<div style="font-family:${FO};font-size:${f(11)}px;line-height:1.7;"><span style="font-weight:600;color:#222;text-transform:uppercase;letter-spacing:.5px;">${k1}:</span> <span style="font-weight:700;">${v1}</span></div>`;
    if(k2&&v2) rows += `<div style="font-family:${FO};font-size:${f(11)}px;line-height:1.7;"><span style="font-weight:600;color:#222;text-transform:uppercase;letter-spacing:.5px;">${k2}:</span> <span style="font-weight:700;">${v2}</span></div>`;
    return `<div style="margin-top:${Math.round(5*s)}px;">${rows}</div>`;
  }

  const slot     = _hasSlot(qrSrc, v.direction);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};border:2px solid #000;box-sizing:border-box;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;right:0;height:${headerH}px;background:#000;color:#fff;display:flex;align-items:center;padding:0 ${p}px;">
      <span style="font-family:${FC};font-size:${f(12)}px;font-weight:400;font-style:normal;letter-spacing:${Math.round(3*s)}px;text-transform:uppercase;">${v.store||'STORE NAME'}</span>
    </div>

    <div style="position:absolute;top:${headerH}px;left:0;right:0;bottom:${footerH}px;display:flex;align-items:stretch;gap:${p}px;padding:${p}px;overflow:hidden;">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          <div data-title style="font-family:${FC};font-size:${f(titleSize(v.name,31))}px;font-weight:400;font-style:normal;line-height:1.15;margin-bottom:${Math.round(4*s)}px;">${v.name||'Product Name'}</div>
          ${v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${f(11)}px;color:#222;font-weight:600;line-height:1.55;">${v.desc}</div>`:''}
          ${extraStacked(v.ex1k,v.ex1v,v.ex2k,v.ex2v)}
        </div>
        <div>
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(5*s)}px;">${priceBlock(v,f,s,82,FC,FO)}</div>`:''}
          ${v.sku?code39Barcode(v.sku,barcodeW,barH):''}
          ${v.sku?`<div style="font-family:${FC};font-size:${f(13)}px;font-weight:400;font-style:normal;color:#111;letter-spacing:${Math.round(1.5*s)}px;margin-top:${Math.round(4*s)}px;">SKU: ${v.sku}</div>`:''}
        </div>
      </div>
      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}
    </div>

    <div style="position:absolute;bottom:0;left:0;right:0;height:${footerH}px;display:flex;align-items:center;justify-content:flex-end;padding:0 ${p}px;">
      <span style="font-family:${FO};font-size:${f(7)}px;color:#bbb;letter-spacing:1px;">${new Date().toLocaleDateString()}</span>
    </div>

  </div>`;
}

// Sizes for one label, then the chosen design. (Was buildLabel() reading the page's globals; now the choices come in `o`.)
export function buildLabel(v: LabelData, labelW: number, labelH: number, qrSrc: string | null, layoutN: number, o: DrawOptions): string {
  const px = labelW, py = labelH;
  const s  = px / 480;
  const f  = (n: number) => Math.max(8, Math.round(n * s));
  const p  = Math.round(10 * s);
  const showSlot = _hasSlot(qrSrc, v.direction);
  // QR size: 37.5% of label height or 22.5% of width, whichever is smaller (qrSize() is the same formula).
  const qrPx  = showSlot ? Math.round(Math.min(py * 0.375, px * 0.225)) : 0;
  const FC = 'contempora-sans-condensed,sans-serif';
  const FO = 'open-sans,sans-serif';
  // Barcode width: fixed by layout so scanners can always read it
  // 1&2-up: 1/5 label width | 4-up: 1/3 label width | 8-up: 1/2 label width
  const barcodeW = layoutN === 8 ? Math.round(labelW / 2)
                 : layoutN === 4 ? Math.round(labelW / 3)
                 :                 Math.round(labelW / 5);
  const tmpl  = o.template;
  if(tmpl === 'minimal') return tmplMinimal(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW);
  if(tmpl === 'price')   return tmplPrice(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW);
  if(tmpl === 'retail')  return tmplRetail(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW);
  return tmplClassic(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW);
}

/* Page size: the 4-up layout turns the page sideways (landscape); all other layouts are portrait. */
export function getPageDims(n: number){
  if(n === 4) return {pageW: LETTER_H, pageH: LETTER_W}; // 1056 × 816 landscape
  return {pageW: LETTER_W, pageH: LETTER_H};             // 816 × 1056 portrait
}

// Size of one label: the page minus margins and gaps, split into cols × rows.
export function getLabelDims(n: number): PageDims {
  const [cols, rows] = LAYOUT_GRID[n]!;
  const {pageW, pageH} = getPageDims(n);
  const labelW = Math.floor((pageW - MARGIN*2 - (cols-1)*GAP) / cols);
  const labelH = Math.floor((pageH - MARGIN*2 - (rows-1)*GAP) / rows);
  return {pageW, pageH, labelW, labelH, cols, rows};
}
